/**
 * OAuth client and token manager.
 *
 * Owns the browser side of the gateway's confidential-client OAuth flow so the
 * session module stays a pure storage seam and the api module stays a pure fetch
 * wrapper. The flow mirrors the native app: the app kicks off sign-in at
 * /v1/auth/oauth/init, the browser is redirected to the identity provider, the
 * gateway bounces the provider callback back to the app callback page with code
 * and state, and the app exchanges code and state for a JWT pair at
 * /v1/auth/login. The gateway is the confidential client: it holds every
 * provider secret and performs the code exchange. The app never sees a provider
 * secret, an id token, or the PKCE verifier.
 *
 * Token management mirrors the native app's interceptor and authenticator: a
 * single-flight refresh rotates a near-expiry access token before an authed read
 * (proactive) and rotates once on a 401 then replays the request (reactive). A
 * refresh that the gateway rejects clears the session and signals the UI to
 * return to the login screen.
 */

import { request, ApiError } from '@/lib/api';
import {
  storeLogin,
  storeRefreshedPair,
  getRefreshToken,
  getDeviceFingerprint,
  clearSession,
} from '@/lib/session';
import { clearSwapIdentityToken } from '@/lib/swapToken';
import { clearPendingSwaps } from '@/store/store';

/** sessionStorage key holding the pending login record across the IdP redirect. */
const PENDING_KEY = 'pmw.oauth.pending';

/**
 * Custom event dispatched on window when a refresh is rejected and the session
 * is cleared, so the boot gate can route back to the login screen. The name is
 * the single coupling point between this module and the boot wiring.
 */
export const SESSION_EXPIRED_EVENT = 'pmw:session-expired';

/**
 * In-flight refresh promise, or null when no refresh is running. A module-level
 * single-flight guard so concurrent authed requests share one refresh rather
 * than each minting a new token pair and racing rotation. This is the browser
 * analog of the native app's refresh mutex.
 */
let inFlightRefresh: Promise<string | null> | null = null;

/** The pending login record held across the IdP redirect. */
interface PendingLogin {
  /** Provider the flow was started for. */
  provider: string;
  /**
   * Server-side session id from init. Held client-side because it is
   * deliberately excluded from the callback URL and is what binds the callback
   * to this client.
   */
  oauthSessionId: string;
  /**
   * The state value from the init authorization URL, checked against the state
   * the IdP returns to defeat CSRF.
   */
  expectedState: string;
  /**
   * The org id this flow requested at init. It is the self-service sentinel for a
   * normal registration and a specific org id when the user is joining a shared
   * org through an invite or admin link. It is held across the redirect so the
   * login call sends the same value the init used, keeping the two halves of the
   * flow consistent. The gateway still enforces membership for any non-sentinel
   * org, so carrying it here cannot widen access.
   */
  requestedOrgId: string;
  /** When the pending record stops being valid, in milliseconds since epoch. */
  expiresAtMillis: number;
}

/** Gateway response from the OAuth init endpoint. */
interface OauthInitResponse {
  oauth_session_id: string;
  authorization_url: string;
  expires_in_seconds: number;
}

/** Gateway response carrying a minted or rotated JWT pair. */
interface TokenPairResponse {
  access_token: string;
  refresh_token: string;
  session_id: string;
  /**
   * The org the token was scoped to. For a self-service signup this is the
   * caller's server-derived personal org, which the client records and uses for
   * subsequent requests rather than a hardcoded default. Absent on a refresh
   * response, which does not re-scope the session.
   */
  org_id?: string;
}

/**
 * Sentinel org id the client sends to request self-service signup. The gateway
 * resolves it to the caller's own server-derived personal org rather than
 * treating it as a named shared org. It is the nil UUID, which is never a real
 * org id, so it cannot collide with a shared org. A first-time user signs in
 * with this value and receives their personal org back in the login response.
 */
const SELF_SERVICE_ORG_SENTINEL = '00000000-0000-0000-0000-000000000000';

/** Matches a canonical lower or upper case UUID, the shape of a real org id. */
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Resolve the org id to request for this sign-in.
 *
 * A normal visitor registers into their own personal org, so the default is the
 * self-service sentinel. A user joining a shared org arrives through a link that
 * names it, for example an admin console or an invite at
 * `https://test.plantmewallet.com/?org=<uuid>`; when the current URL carries a
 * well-formed `org` query parameter that is not the sentinel, that org is
 * requested instead. Sending a shared org is safe because the gateway still
 * requires an active membership for any non-sentinel org and refuses a
 * non-member, so this only selects which membership the login is checked
 * against, never grants one. A malformed value falls back to the sentinel rather
 * than being forwarded as a bogus org.
 *
 * The URL query string is a parameter defaulting to the live location so the
 * decision can be unit tested without a DOM. The `window` reference is only
 * evaluated when the argument is omitted, which is the runtime path.
 */
export function resolveRequestedOrgId(search: string = window.location.search): string {
  const raw = new URLSearchParams(search).get('org');
  if (raw === null) {
    return SELF_SERVICE_ORG_SENTINEL;
  }
  const candidate = raw.trim().toLowerCase();
  return UUID_PATTERN.test(candidate) ? candidate : SELF_SERVICE_ORG_SENTINEL;
}

/**
 * Normalize an org id typed on the sign-in screen to the canonical lower-case
 * UUID the gateway expects, or null when it is blank or not a UUID. An admin
 * uses the sign-in field to target a shared org directly, which is more reliable
 * than a URL query parameter that a redirect can drop before the login runs.
 * Sending a shared org is safe: the gateway still requires an active membership
 * and refuses a non-member, so this only chooses which membership the login is
 * checked against.
 *
 * @param raw The user-entered org id.
 * @returns The normalized UUID, or null when the entry is blank or malformed.
 */
export function normalizeOrgId(raw: string): string | null {
  const candidate = raw.trim().toLowerCase();
  return UUID_PATTERN.test(candidate) ? candidate : null;
}

/** Callback query parameters the gateway echoes back to the callback page. */
export interface CompleteLoginParams {
  /** Provider name the gateway echoed. */
  provider?: string;
  /** Authorization code from the IdP. */
  code?: string;
  /** State value the IdP returned. */
  state?: string;
  /** Error code when the IdP denied the request. */
  error?: string;
}

/**
 * Begin sign-in for a provider. Allocates a server-side OAuth session, stashes
 * the pending record so the callback can complete the flow, and navigates the
 * browser to the provider's authorization URL.
 *
 * @param provider Provider name, for example "google".
 * @returns Resolves only if navigation does not occur; normally the call ends by
 *   navigating away.
 * @throws {ApiError} When init fails, for example an unregistered provider.
 */
export async function beginLogin(provider: string, orgOverride?: string): Promise<void> {
  // Choose the org to sign in to, in order of precedence: an org typed on the
  // sign-in screen (the admin path, most reliable since it does not depend on a
  // URL surviving the login navigation), then a well-formed org query parameter
  // on the current URL, then the self-service sentinel so a first-time user signs
  // in to their own personal org. The value is held in the pending record so the
  // later login call sends the same org this init used.
  const typedOrg = orgOverride ? normalizeOrgId(orgOverride) : null;
  const requestedOrgId = typedOrg ?? resolveRequestedOrgId();

  const init = await request<OauthInitResponse>('/v1/auth/oauth/init', {
    method: 'POST',
    auth: false,
    body: { provider, org_id: requestedOrgId, client_type: 'web' },
  });

  const authUrl = init.authorization_url;
  const expectedState = new URL(authUrl).searchParams.get('state') ?? '';
  const ttlSeconds = Number.isFinite(init.expires_in_seconds) ? init.expires_in_seconds : 600;

  const pending: PendingLogin = {
    provider,
    oauthSessionId: init.oauth_session_id,
    expectedState,
    requestedOrgId,
    expiresAtMillis: Date.now() + ttlSeconds * 1000,
  };
  writePending(pending);

  // Leave the SPA entirely: the authorization URL is on the provider's origin.
  // sessionStorage survives this top-level navigation away and back, which is
  // why the pending record lives there rather than in a module variable.
  window.location.assign(authUrl);
}

/**
 * Complete sign-in from the callback page. Reads and clears the pending record,
 * verifies the returned state against it, exchanges code and state for a JWT
 * pair, and records the session. The single-use code and the CSRF state binding
 * are both enforced by the gateway; this checks state locally first so a forged
 * callback never reaches the exchange.
 *
 * @param params Callback query parameters.
 * @returns Resolves once the session is stored.
 * @throws {Error} With a user-facing message on any failure.
 */
export async function completeLogin({ provider, code, state, error }: CompleteLoginParams): Promise<void> {
  const pending = readPending();

  // An IdP denial carries an error rather than a code. Surface it and stop
  // before touching the pending record's single-use session.
  if (error && error.trim() !== '') {
    clearPending();
    throw new Error(`Sign-in was cancelled or denied (${error}).`);
  }

  if (!pending) {
    throw new Error('No sign-in is in progress. Start again from the sign-in screen.');
  }
  if (Date.now() > pending.expiresAtMillis) {
    clearPending();
    throw new Error('This sign-in attempt expired. Start again from the sign-in screen.');
  }
  if (!code || code.trim() === '' || !state || state.trim() === '') {
    throw new Error('The sign-in response was incomplete. Start again from the sign-in screen.');
  }

  // Compare the returned state to the expected state without clearing the
  // pending record on mismatch, so a forged callback cannot burn the one-shot
  // session and lock out the real redirect that may still arrive.
  if (state !== pending.expectedState) {
    throw new Error('The sign-in response failed a security check. Start again from the sign-in screen.');
  }

  // The state checked out, so this is the genuine redirect. Clear the pending
  // record now to make the exchange single-use from the client side too.
  clearPending();

  const resolvedProvider = provider && provider.trim() !== '' ? provider : pending.provider;

  const login = await request<TokenPairResponse>('/v1/auth/login', {
    method: 'POST',
    auth: false,
    body: {
      provider: resolvedProvider,
      // Send the same org the init used. For a sentinel this requests
      // self-service signup, and the gateway resolves the caller's own personal
      // org from the verified identity and returns it below: a first-time user is
      // enrolled into their personal org and a returning user resolves the
      // existing membership. For a named shared org the gateway checks membership
      // and refuses a non-member, so this never grants access on its own.
      org_id: pending.requestedOrgId,
      oauth_session_id: pending.oauthSessionId,
      code,
      state,
      device_fingerprint: getDeviceFingerprint(),
    },
  });

  // Record the org the gateway scoped the token to, taken from the login
  // response rather than the value we sent, so the session tracks the
  // server-derived personal org for a self-service signup. Fall back to the
  // requested org only if an older gateway omits the field; the org that
  // authorizes reads still lives in the signed token claim regardless.
  storeLogin({
    accessToken: login.access_token,
    refreshToken: login.refresh_token,
    sessionId: login.session_id,
    orgId: login.org_id ?? pending.requestedOrgId,
    provider: resolvedProvider,
  });
}

/**
 * Proactively refresh the access token when it is within a leeway of expiry.
 * Installed on the api layer so an authed request always carries a token with
 * headroom. A no-op when no refresh token is stored or the current token is
 * still fresh.
 *
 * @param leewaySeconds Refresh when the token expires within this many seconds.
 *   Defaults to sixty.
 * @returns Resolves once any needed refresh settles. A failed refresh does not
 *   throw here; the subsequent request observes the result.
 */
export async function ensureFreshAccessToken(leewaySeconds = 60): Promise<void> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return;

  // Only refresh when a current access token is missing or near expiry, so a
  // healthy token is left untouched and the gateway is not rotated needlessly.
  if (!shouldRefresh(leewaySeconds)) return;

  try {
    await refreshSingleFlight();
  } catch {
    // A failed proactive refresh is swallowed: the request that follows runs
    // with whatever token is present and surfaces a 401 if the session is gone.
  }
}

/**
 * Reactively refresh after a 401, used by the api layer to rotate once and let
 * the request replay. Returns the fresh access token on success so the caller
 * can replay with it, or null when the session could not be refreshed.
 *
 * @param failedToken The token the failed request used, ignored here but
 *   accepted to mirror the native authenticator signature.
 * @returns A fresh access token, or null when refresh failed and the session was
 *   cleared.
 */
export async function refreshAfter401(failedToken: string | null): Promise<string | null> {
  void failedToken;
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;
  try {
    return await refreshSingleFlight();
  } catch {
    // refreshSingleFlight clears the session and signals expiry on a hard
    // rejection. Returning null tells the api layer not to replay.
    return null;
  }
}

/**
 * Sign out. Best-effort: the gateway blocklists the access token's jti, then the
 * local session is cleared regardless of the network outcome so the client is
 * always logged out locally. Dispatches the session-expired event so the boot
 * gate routes back to the login screen.
 *
 * @returns Resolves once the local session is cleared.
 */
export async function logout(): Promise<void> {
  try {
    await request('/v1/auth/logout', { method: 'POST', body: {}, retry: false });
  } catch {
    // A failed logout call still clears the local session below; the access
    // token expires on its own even if the blocklist write did not land.
  }
  clearSession();
  // Drop the cached swap-identity token so one minted for this session is not
  // reused after sign-out; the next swap call mints a fresh one.
  clearSwapIdentityToken();
  // Drop any tracked in-flight swaps so the next account that signs in on this
  // browser never resumes polling or displaying a swap that was not theirs.
  clearPendingSwaps();
  signalSessionExpired();
}

/**
 * Run a refresh under the single-flight guard. Concurrent callers share the one
 * in-flight promise. On a 401 from the refresh endpoint the session is cleared
 * and the expiry signal is dispatched, then the error is rethrown so the caller
 * can decide what to do. A transient or offline error rethrows without clearing
 * the session, so a brief network blip does not log the user out.
 *
 * @returns The fresh access token, or null when none was returned.
 */
function refreshSingleFlight(): Promise<string | null> {
  if (inFlightRefresh) return inFlightRefresh;

  inFlightRefresh = (async (): Promise<string | null> => {
    // Re-read the refresh token inside the guard so a refresh that started just
    // before a rotation picks up the latest stored value.
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      clearSession();
      clearSwapIdentityToken();
      signalSessionExpired();
      throw new ApiError('no refresh token', { status: 401 });
    }

    try {
      const pair = await request<TokenPairResponse>('/v1/auth/refresh', {
        method: 'POST',
        auth: false,
        retry: false,
        body: { refresh_token: refreshToken },
      });
      storeRefreshedPair({
        accessToken: pair.access_token,
        refreshToken: pair.refresh_token,
      });
      return pair.access_token ?? null;
    } catch (err) {
      const apiErr = err instanceof ApiError ? err : new ApiError(String(err));
      // A 401 means the refresh token is spent or revoked: the session is over.
      // A transient failure leaves the session intact for a later retry.
      if (apiErr.status === 401) {
        clearSession();
        clearSwapIdentityToken();
        signalSessionExpired();
      }
      throw apiErr;
    }
  })();

  // Clear the guard once the refresh settles, regardless of outcome, so the next
  // refresh can start fresh.
  void inFlightRefresh.finally(() => {
    inFlightRefresh = null;
  });
  return inFlightRefresh;
}

/**
 * Decide whether the current access token is missing or close enough to expiry
 * to refresh. The expiry is read from the JWT's exp claim without verifying the
 * signature, which is only a heuristic to schedule the refresh; the gateway
 * re-verifies the token on every call. A token whose exp cannot be read is
 * treated as needing a refresh so a malformed token does not get stuck.
 *
 * @param leewaySeconds Refresh when expiry is within this many seconds.
 * @returns True when a refresh should run.
 */
function shouldRefresh(leewaySeconds: number): boolean {
  const token = readAccessTokenForExpiry();
  if (!token) return true;
  const exp = readJwtExpSeconds(token);
  if (exp === null) return true;
  const nowSeconds = Math.floor(Date.now() / 1000);
  return exp - nowSeconds <= leewaySeconds;
}

/**
 * Read the current access token solely to inspect its expiry. The session module
 * does not expose the access token by value, so this reaches through the api
 * provider hook, which returns whatever token the next request would send.
 *
 * @returns The current access token, or null.
 */
function readAccessTokenForExpiry(): string | null {
  // Read through the injected getter rather than a static import to avoid an
  // import cycle with the boot wiring; the provider hook is the same value the
  // api layer would attach. session.getToken returns the active access token for
  // a real session.
  return getActiveAccessToken();
}

/**
 * The active access token getter, wired by the boot module from the session
 * module. Kept as a hook rather than a static import so this module has no
 * import cycle with the boot wiring and tests can inject a token.
 */
let getActiveAccessToken: () => string | null = () => null;

/**
 * Install the active-access-token getter. The boot module wires this to the
 * session module's getToken so the expiry heuristic reads the live token.
 *
 * @param getter Returns the current access token.
 */
export function setActiveAccessTokenGetter(getter: () => string | null): void {
  getActiveAccessToken = getter;
}

/**
 * Read the exp claim (seconds since the epoch) from a JWT without verifying it.
 * Decodes the middle segment as base64url JSON. Returns null when the token is
 * not a well-formed three-segment JWT or carries no numeric exp.
 *
 * @param token The JWT to inspect.
 * @returns The exp in seconds, or null when unreadable.
 */
function readJwtExpSeconds(token: string): number | null {
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const json = base64UrlDecode(parts[1]);
    const claims = JSON.parse(json) as { exp?: unknown };
    return typeof claims.exp === 'number' ? claims.exp : null;
  } catch {
    return null;
  }
}

/**
 * Decode a base64url string to its UTF-8 text. Pads to a multiple of four and
 * translates the URL-safe alphabet back to standard base64 before decoding.
 *
 * @param input base64url-encoded data.
 * @returns The decoded UTF-8 string.
 */
function base64UrlDecode(input: string): string {
  const padded = input.replace(/-/g, '+').replace(/_/g, '/');
  const pad = padded.length % 4 === 0 ? '' : '='.repeat(4 - (padded.length % 4));
  const binary = atob(padded + pad);
  // Reinterpret the binary string as UTF-8 so multibyte claim values decode
  // correctly rather than as Latin-1.
  const bytes = Uint8Array.from(binary, (ch) => ch.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

/** Persist the pending login record for the IdP round trip. */
function writePending(pending: PendingLogin): void {
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));
    }
  } catch {
    // sessionStorage being unavailable means the callback cannot complete; the
    // callback view surfaces the missing-pending error if so.
  }
}

/**
 * Read the pending login record set before the IdP redirect.
 *
 * @returns The pending record, or null when absent or unparseable.
 */
function readPending(): PendingLogin | null {
  try {
    if (typeof sessionStorage === 'undefined') return null;
    const raw = sessionStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PendingLogin> | null;
    if (
      parsed &&
      typeof parsed.provider === 'string' &&
      typeof parsed.oauthSessionId === 'string' &&
      typeof parsed.expectedState === 'string' &&
      typeof parsed.expiresAtMillis === 'number'
    ) {
      return parsed as PendingLogin;
    }
  } catch {
    // A corrupt record is treated as no pending login.
  }
  return null;
}

/** Remove the pending login record. */
function clearPending(): void {
  try {
    if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem(PENDING_KEY);
  } catch {
    // Best-effort: a record that cannot be removed simply expires by its ttl.
  }
}

/** Dispatch the session-expired event so the boot gate routes to login. */
function signalSessionExpired(): void {
  try {
    if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
      window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
    }
  } catch {
    // A failure to dispatch is non-fatal; the next gate check still reflects the
    // cleared session.
  }
}
